import { FunnelEvents } from "@/api/insights";
import { LoadedEventDefinition } from "@/types/global";

/**
 * The enriched list is rebuilt nightly from a replica of the Event Library, so
 * an event defined since the last rebuild is missing from it for up to about
 * two days. Library events from the last three days fill that gap with a day
 * of margin for a late or failed rebuild. Saving one works: the backend
 * accepts any event in the project.
 */
const UNPROCESSED_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

type QueryState<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
};

export type EventOptionsInput = {
  enriched: QueryState<FunnelEvents[]>;
  library: QueryState<LoadedEventDefinition[]>;
  savedSelection: Omit<QueryState<unknown>, "data">;
  now: number;
};

export type EventOptions =
  | { status: "loading" }
  | { status: "error" }
  | { status: "empty" }
  | {
      status: "ready";
      events: FunnelEvents[];
      /** Some events come from the Event Library without traffic stats. */
      hasUnprocessed: boolean;
    };

function fromLibrary(def: LoadedEventDefinition): FunnelEvents {
  return {
    eventDefId: def.id,
    name: def.name,
    funnelStage: "",
    conversionSource: "",
    isRecommended: false,
    isOther: false,
    isHidden: false,
    description: def.description,
    totalEventsDelivered: 0,
    isHighVolumeEvent: false,
    adPlatformsDelivered: 0,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: false,
    hasNoRecentTraffic: false,
    tags: def.tags ?? {},
    type: "",
  };
}

function isRecent(def: LoadedEventDefinition, now: number): boolean {
  if (!def.created_at) {
    return false;
  }
  // `created_at` is typed as a number but arrives as an RFC 3339 string.
  return now - new Date(def.created_at).getTime() < UNPROCESSED_WINDOW_MS;
}

/**
 * Picker options are the enriched event list plus Event Library events that
 * haven't reached it yet. Older library events missing from the enriched list
 * stay out: the backend trims low-value events for large projects on purpose.
 */
export function resolveEventOptions({
  enriched,
  library,
  savedSelection,
  now,
}: EventOptionsInput): EventOptions {
  if (savedSelection.isLoading || enriched.isLoading || library.isLoading) {
    return { status: "loading" };
  }
  // The save echoes the revenue default from the saved selection, so it can't
  // proceed without it.
  if (savedSelection.isError || (enriched.isError && library.isError)) {
    return { status: "error" };
  }

  const enrichedEvents = (enriched.data ?? []).filter((e) => !e.isHidden);
  const known = new Set(enrichedEvents.map((e) => e.eventDefId));
  const trustLibraryFully = enriched.isError || enrichedEvents.length === 0;
  const unprocessed = (library.data ?? [])
    .filter(
      (def) =>
        !def.is_hidden &&
        !def.deleted_at &&
        !known.has(def.id) &&
        (trustLibraryFully || isRecent(def, now)),
    )
    .map(fromLibrary);

  const events = [...enrichedEvents, ...unprocessed];
  if (events.length === 0) {
    // With one source down, an empty result can't be trusted.
    return enriched.isError || library.isError
      ? { status: "error" }
      : { status: "empty" };
  }
  return { status: "ready", events, hasUnprocessed: unprocessed.length > 0 };
}
